import { ApiErrorKeys,isApiErrorKey,isDynamicApiErrorKey } from "../constants/apiErrorKeys";
import {
HTTP_STATUS_BAD_REQUEST,
HTTP_STATUS_CONFLICT,
HTTP_STATUS_FORBIDDEN,
HTTP_STATUS_INTERNAL_SERVER_ERROR,
HTTP_STATUS_NOT_FOUND,
HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE,
HTTP_STATUS_REQUEST_TIMEOUT,
HTTP_STATUS_SERVICE_UNAVAILABLE,
HTTP_STATUS_TOO_MANY_REQUESTS,
HTTP_STATUS_UNAUTHORIZED
} from "../constants/httpStatus";
import { ApiError,parseErrorKeyFromBodyText } from "./client";
import { HTTP_CLIENT_ERROR_MIN, HTTP_SERVER_ERROR_MIN } from "./httpStatusRanges";

export type ApiErrorClass = "UserExpected" | "Recoverable" | "SystemFailure";

export type ApiErrorClassification = {
  class: ApiErrorClass;
  errorKey?: string;
  reason: string;
};

function isHtmlBody(bodyText: string | null | undefined): boolean {
  const txt = (bodyText ?? "").trim();
  return /^\s*</.test(txt) || /<html/i.test(txt);
}

function isExpectedAppointmentCapacityError(error: ApiError): boolean {
  const value = `${error.message ?? ""}\n${error.bodyText ?? ""}`.toLowerCase();
  return (
    value.includes("kapazität nicht ausreichend") ||
    value.includes("gewählte belegung nicht verfügbar") ||
    value.includes("keine passende belegung verfügbar") ||
    value.includes("nicht alle belegungen verfügbar") ||
    value.includes("passt nicht zu dieser terminart") ||
    value.includes("resource capacity is insufficient") ||
    value.includes("insufficient capacity")
  );
}

export function getApiErrorKey(error: ApiError): string | undefined {
  const fromBody = parseErrorKeyFromBodyText(error.bodyText ?? "");
  if (fromBody) return fromBody;
  const msg = (error.message ?? "").trim();
  return isApiErrorKey(msg) || isDynamicApiErrorKey(msg) ? msg : undefined;
}

export function classifyApiError(error: ApiError): ApiErrorClassification {
  const errorKey = getApiErrorKey(error);

  if (error.status === HTTP_STATUS_FORBIDDEN && errorKey === ApiErrorKeys.TenantImportCommitInProgress) {
    return { class: "Recoverable", errorKey, reason: "tenant-import-commit-in-progress" };
  }

  if (error.status === HTTP_STATUS_FORBIDDEN && errorKey === ApiErrorKeys.TenantBackupRestoreInProgress) {
    return { class: "Recoverable", errorKey, reason: "tenant-backup-restore-in-progress" };
  }

  if (error.status === HTTP_STATUS_SERVICE_UNAVAILABLE) {
    return { class: "Recoverable", errorKey, reason: "service-unavailable-maintenance" };
  }

  if (error.status === HTTP_STATUS_REQUEST_TIMEOUT || error.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
    return { class: "Recoverable", errorKey, reason: "retryable-status" };
  }

  if (isHtmlBody(error.bodyText)) {
    return { class: "SystemFailure", errorKey, reason: "html-gateway-response" };
  }

  if (error.status === 0 || error.status >= HTTP_STATUS_INTERNAL_SERVER_ERROR) {
    return { class: "SystemFailure", errorKey, reason: "server-or-network-failure" };
  }

  if (
    error.status === HTTP_STATUS_UNAUTHORIZED ||
    // ASP.NET Core ForbidResult liefert auch ohne Fehlerschlüssel eine reguläre Zugriffssperre.
    error.status === HTTP_STATUS_FORBIDDEN ||
    error.status === HTTP_STATUS_NOT_FOUND ||
    error.status === HTTP_STATUS_CONFLICT ||
    error.status === HTTP_STATUS_REQUEST_ENTITY_TOO_LARGE
  ) {
    return { class: "UserExpected", errorKey, reason: "expected-status" };
  }

  if (error.status === HTTP_STATUS_BAD_REQUEST && errorKey) {
    return { class: "UserExpected", errorKey, reason: "known-error-key" };
  }

  if (error.status === HTTP_STATUS_BAD_REQUEST && isExpectedAppointmentCapacityError(error)) {
    return { class: "UserExpected", errorKey, reason: "appointment-capacity-or-availability" };
  }

  if (error.status >= HTTP_CLIENT_ERROR_MIN && error.status < HTTP_SERVER_ERROR_MIN) {
    return { class: "SystemFailure", errorKey, reason: "unknown-client-error" };
  }

  return { class: "SystemFailure", errorKey, reason: "unknown-api-error" };
}

export function shouldLogApiErrorToBackend(error: ApiError): boolean {
  // Eine vom Backend beantwortete 5xx-Anfrage mit Request-ID wurde dort bereits mitsamt
  // Exception protokolliert. Die erneute Meldung über /api/v1/client-errors wäre derselbe Vorfall.
  if (error.status >= HTTP_STATUS_INTERNAL_SERVER_ERROR && error.requestId) return false;
  return classifyApiError(error).class === "SystemFailure";
}
